import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import TextSection from "./TextSection";
import { useTranslation } from "react-i18next";
import { get } from "../../../services/apiService";
import useWindowSize from "../../../hooks/useWindowSize";
import tick from "../../../assets/images/website-flow/Checkmark.png";
import ScreenLoader from "../../common/ScreenLoader";
import i18next from "i18next";
import { useAuth } from "../../../hooks/useAuth";

const PricingPlans = ({
  screenWidth,
  id,
  theme,
  buttonLink,
  showText,
  onSelect,
  showPlansListing,
}) => {
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const { currentLang } = useAuth();
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState("monthly");
  const [isLoading, setIsLoading] = useState(true);
  const [activePlanId, setActivePlanId] = useState(null);

  const getPlans = useCallback(async () => {
    const response = await get("plans/all", {
      headers: {
        "Accept-Language": currentLang
      }
    }, onLoading);
    return response.data;
  }, [currentLang]);

  const onLoading = (loading) => {
    setIsLoading(loading);
  };

  useEffect(() => {
    const userId = sessionStorage.getItem("userId");
    
    const fetchData = async () => {
      try {
        // Fetch plans
        const plansData = await getPlans();
        setPlans(plansData);
        
        // Fetch user's current plan if userId exists
        if (userId) {
          const planResponse = await get(`users/${userId}/plan`, {}, onLoading);
          setActivePlanId(planResponse?.data?.plan?.id);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [getPlans]); // Only depend on getPlans since userId is accessed inside

  return (
    <>
      <Box
        mt={5}
        textAlign="center"
        justifyItems="center"
        justifyContent="center"
        sx={{
          paddingLeft: width > 1200 ? "7%" : "1%",
          paddingRight: width > 1200 ? "7%" : "1%",
          marginBottom: "5%",
        }}
      >
        <Button
          variant="outlined"
          sx={{
            backgroundColor:
              activePlan === "monthly" ? "rgba(49, 115, 177, 1)" : "white",
            color: activePlan === "monthly" ? "white" : "black",
            fontSize: "20px",
            fontWeight: "700",
            width: width > 1000 ? "16%" : width > 500 ? "30%" : "50%",
            borderRadius: "10px",
            fontFamily: "Tajawal-Bold",
            borderColor: "black",
          }}
          onClick={() => setActivePlan("monthly")}
        >
        {i18next.t("BILLED_MONTHLY_TEXT")}
        </Button>
        <Button
          variant="outlined"
          sx={{
            backgroundColor:
              activePlan === "yearly" ? "rgba(49, 115, 177, 1)" : "white",
            color: activePlan === "yearly" ? "white" : "black",
            fontSize: "20px",
            fontWeight: "700",
            width: width > 1000 ? "16%" : width > 500 ? "30%" : "50%",
            borderRadius: "10px",
            fontFamily: "Tajawal-Bold",
            marginLeft: currentLang === "en" && "1%",
            marginRight: currentLang === "ar" && "1%",
            borderColor: "black",
            marginTop: width < 500 && "3%",
          }}
          onClick={() => setActivePlan("yearly")}
        >
            {i18next.t("BILLED_YEARLY_TEXT")}
        </Button>
      </Box>
      <Box sx={{ backgroundColor: "#F7FAFE", py: 5 }} id={id}>
        <Grid
          container
          spacing={2}
          justifyContent="center"
          mb={4}
          sx={{
            paddingLeft: screenWidth > 1200 ? "14%" : "5%",
            paddingRight: screenWidth > 1200 ? "14%" : "5%",
          }}
        >
          {showText && <TextSection />}
          {isLoading ? (
            <ScreenLoader />
          ) : plans?.length > 0 ? (
            plans?.map((plan, index) => (
              <Grid
                item
                size={{ xl: 4, lg: 4, md: 6, sm: 6, xs: 12 }}
                key={plan.id || index}
              >
                <Box
                  sx={{
                    border: plan.isRecommended
                      ? "transparent"
                      : "1px solid #3173B1",
                    borderRadius: "8px",
                    p: 4,
                    backgroundColor: plan.isRecommended ? "white" : "#F7FAFE",
                    position: "relative",
                    textAlign: "center",
                    height: "100%",
                    width: "100%",
                    fontFamily: "Tajawal",
                    marginTop:
                      plan.isRecommended && screenWidth > 1200 ? "-7%" : "2%",
                  }}
                >
                  {/* Recommended Label */}
                  {plan.isRecommended && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: screenWidth > 1200 ? "-20px" : "0px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#29b6f6",
                        color: "#fff",
                        borderRadius: "8px",
                        px: 2,
                        py: 0.5,
                        fontSize: "24px",
                        fontWeight: "700",
                        width: "100%",
                        height: "64px",
                        fontFamily: "Tajawal",
                        borderTopLeftRadius: "20px",
                        borderBottomRightRadius: "20px",
                        paddingTop: "4%",
                      }}
                    >
                      {t("Recomended")}
                    </Box>
                  )}
                  {/* Plan Title */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      paddingTop: screenWidth > 600 ? "4%" : "2%",
                      fontFamily: "Tajawal-Regular",
                      marginTop: screenWidth > 1200 ? "0%" : "12%",
                    }}
                  >
                    {t(plan.planName)}
                  </Typography>
                  {/* Price */}
                  <Typography
                    variant="h4"
                    sx={{
                      marginTop: "6%",
                      fontWeight: "bold",
                      mb: 1,
                      fontFamily: "Tajawal-Bold",
                    }}
                  >
                    {activePlan === "monthly"
                      ? "USD " + plan.prices[0].price
                      : "USD " + plan.prices[1].price}{" "}
                    <span
                      style={{
                        fontSize: "18px",
                        fontFamily: "Tajawal-Regular",
                      }}
                    >
                    {`/ ${i18next.t(activePlan === "monthly" ? "MONTHLY_TEXT" : "YEARLY_TEXT")}`}
                    </span>
                  </Typography>
                  {/* Select Button */}
                  <Button
                    onClick={() => {
                      setActivePlanId(plan.id);

                      if (buttonLink) {
                        navigate(buttonLink);
                      } else {
                        onSelect({
                          planId: plan.id,
                          planPriceType: activePlan,
                          subscriptionDuration: 1,
                          skipUpdatingStep: showPlansListing === true,
                        });
                      }
                    }}
                    variant="outlined"
                    color={plan.isRecommended ? "primary" : "inherit"}
                    sx={{
                      width: "98%",
                      mb: 2,
                      marginTop: "3%",
                      fontFamily: "Tajawal-Light",
                      backgroundColor:
                        activePlanId === plan.id ? "#004D8A" : "none",
                      color: activePlanId === plan.id ? "white" : "inherit",
                      fontWeight: activePlanId === plan.id ? 800 : 400,
                    }}
                  >
                    {t("SELECT_TEXT")}
                  </Button>
                  {/* Features List */}
                  {plan.features.map(
                    (feature, i) =>
                      feature.planFeature && (
                        <Box key={i} sx={{ display: "flex", marginTop: "6%" }}>
                          <img
                            src={tick}
                            alt="tick"
                            style={{
                              width: "25px",
                              height: "25px",
                              marginLeft: "9%",
                              marginRight: "6%",
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#808080",
                              fontWeight: "700",
                              fontSize: "16px",
                              fontFamily: "Tajawal-Regular",
                              mb: 1,
                              textAlign:
                                currentLang === "en" ? "left" : "right",
                            }}
                          >
                            {t(feature.planFeature)}
                          </Typography>
                        </Box>
                      )
                  )}
                </Box>
              </Grid>
            ))
          ) : (
            <Grid item size={{ xs: 12 }}>
              <Typography
                sx={{
                  textAlign: "center",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                No Plans to Show
              </Typography>
              <Typography
                sx={{
                  textAlign: "center",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                On this Screen you can select plan for a month or a year.
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
};

export default PricingPlans;